import { format, isValid, parseISO } from 'date-fns';
import {
  MESSAGE_TYPES,
  SENDER_TYPES,
} from 'dashboard/components-next/message/constants';

/**
 * Checks if a message is a transcript message (private note with jarabot transcript).
 * Works with both full message objects and individual properties.
 *
 * @param {Object} params
 * @param {boolean} params.private - Whether the message is private
 * @param {Object} params.contentAttributes - The message content attributes
 * @returns {boolean}
 */
export function isTranscriptMessage({ private: isPrivate, contentAttributes }) {
  return !!isPrivate && !!contentAttributes?.jarabot?.transcript;
}

/**
 * Checks whether the message is a Jara bot message that the current agent can
 * rate (👍/👎). Mirrors `Message#jarabot_rateable?` on the backend: only
 * outgoing AgentBot messages, either public bot responses or private copilot
 * notes.
 *
 * @param {Object} message - Message object (camelCase keys)
 * @returns {boolean}
 */
export function isJarabotRateableMessage(message) {
  return (
    message?.sender?.type === SENDER_TYPES.AGENT_BOT &&
    message.messageType === MESSAGE_TYPES.OUTGOING &&
    !message.private
  );
}

/**
 * Checks whether the message carries a localized_content payload whose content
 * should be generated on render instead of being read from the persisted
 * `content` field.
 *
 * @param {Object} contentAttributes
 * @returns {boolean}
 */
export function hasLocalizedContent(contentAttributes) {
  return !!contentAttributes?.localizedContent?.type;
}

const COPILOT_KEY = 'CONVERSATION.MESSAGE_EVENT.VOICE.COPILOT';
const ACTIVITY_I18N_SCOPE = 'CONVERSATION.MESSAGE_EVENT.ACTIVITY';

function resolveTranslateValue(value, t) {
  if (
    value &&
    typeof value === 'object' &&
    value.translate === true &&
    typeof value.key === 'string'
  ) {
    return t(value.key);
  }
  return value;
}

function resolveActivityData(data, t) {
  if (!data || typeof data !== 'object') return data;
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      resolveTranslateValue(value, t),
    ])
  );
}

function formatDuration(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

function formatExtensionLine(line, t) {
  let row = `${line.index}) *${line.extension ?? ''}*`;
  if (line.extensionName) row += ` ${line.extensionName}`;
  if (line.extensionType) {
    const typeKey = String(line.extensionType).toUpperCase();

    row += ` (${t(`${COPILOT_KEY}.EXTENSION_TYPES.${typeKey}`)})`;
  }
  if (line.talkDuration != null && line.talkDuration > 0) {
    row += ` - ${formatDuration(line.talkDuration)}`;
  } else if (line.outcome) {
    // An operator whose phone rang but who did not take the call: say whether
    // they rejected it or let it ring, and for how long.
    const outcomeKey = String(line.outcome).toUpperCase();

    row += ` - ${t(`${COPILOT_KEY}.EXTENSION_OUTCOMES.${outcomeKey}`, {
      duration: formatDuration(line.ringDuration),
    })}`;
  }
  return row;
}

function generateExtensionDetailsContent(data, t) {
  const lines = Array.isArray(data?.lines) ? data.lines : [];

  const parts = [
    t('CONVERSATION.MESSAGE_EVENT.VOICE.COPILOT.CALLED_EXTENSIONS'),
  ];
  lines.forEach(line => parts.push(formatExtensionLine(line, t)));

  return parts.join('\n');
}

/**
 * Generates the display content for messages whose
 * `content_attributes.localized_content` is set. The payload shape is
 * `{ type, data?, error? }`.
 *
 * @param {Object} contentAttributes - Message content attributes (camelCased).
 * @param {(key: string, values?: Object) => string} t - Vue-i18n `t` function.
 * @returns {string|null} The rendered content (plain text or markdown), or null
 *   when there is no localized_content / no renderable content.
 */
export function generateLocalizedContent(contentAttributes, t) {
  const localizedContent = contentAttributes?.localizedContent;
  if (!localizedContent?.type) return null;

  const { type, data = {}, error = {}, i18nKey } = localizedContent;

  switch (type) {
    case 'activity': {
      if (!i18nKey) return null;

      return t(
        `${ACTIVITY_I18N_SCOPE}.${i18nKey}`,
        resolveActivityData(data, t)
      );
    }

    case 'error': {
      const reason = error?.reason;
      if (!reason) return null;

      return t(`${COPILOT_KEY}.${String(reason).toUpperCase()}`);
    }

    case 'recording_urls': {
      const urls = Array.isArray(data?.urls) ? data.urls : [];
      return urls
        .map((url, index) => `[Recording #${index + 1}](${url})`)
        .join('\n\n');
    }

    // The call recordings of the message were deleted (Messages::DeleteCallRecordingService).
    case 'recording_deleted': {
      const deletedAt = data?.deletedAt ? parseISO(data.deletedAt) : null;
      return t(`${COPILOT_KEY}.RECORDING_DELETED`, {
        name: data?.userName ?? '',
        date:
          deletedAt && isValid(deletedAt)
            ? format(deletedAt, 'd.M.yyyy H:mm')
            : '',
      });
    }

    case 'transcript': {
      const transcript = Array.isArray(data?.transcript) ? data.transcript : [];
      return transcript
        .map(line => {
          const roleKey = String(line?.role ?? '').toUpperCase();
          const translatedRole = roleKey
            ? t(`${ACTIVITY_I18N_SCOPE}.SUMMARY.ROLE.${roleKey}`)
            : '';
          return `**${translatedRole}**:\n${line?.text ?? ''}`;
        })
        .join('\n\n');
    }

    case 'summary': {
      return data?.summary ?? '';
    }

    case 'extension_details':
      return generateExtensionDetailsContent(data, t);

    default:
      return t(`${COPILOT_KEY}.${type.toUpperCase()}`, data);
  }
}

/**
 * Returns the content to display for a message, substituting generated content
 * for messages whose content is provided via `content_attributes.localized_content`
 * instead of the persisted `content` field.
 *
 * @param {Object} message - Message object (may use snake_case or camelCase keys).
 * @param {(key: string, values?: Object) => string} t - Vue-i18n `t` function.
 * @returns {string|null}
 */
export function getEffectiveMessageContent(message, t) {
  const { contentAttributes } = message ?? {};
  if (hasLocalizedContent(contentAttributes)) {
    const generated = generateLocalizedContent(contentAttributes, t);
    if (generated) return generated;
  }
  return message?.content;
}
